import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AppShell } from "./components/layout/AppShell";

const Login = lazy(() => import("./routes/login"));
const AuthVerify = lazy(() => import("./routes/auth-verify"));
const AuthSsoComplete = lazy(() => import("./routes/auth-sso-complete"));
const ProjectsList = lazy(() => import("./routes/projects-list"));
const ProjectNew = lazy(() => import("./routes/project-new"));
const ProjectDetail = lazy(() => import("./routes/project-detail"));
const ProjectEdit = lazy(() => import("./routes/project-edit"));
const ProjectSeedUrls = lazy(() => import("./routes/project-seed-urls"));
const ScanDetailLayout = lazy(() => import("./routes/scan-detail-layout"));
const ScanDetailIndex = lazy(() => import("./routes/scan-detail-index"));
const ScanPagesTab = lazy(() => import("./routes/scan-pages-tab"));
const ScanAssetsTab = lazy(() => import("./routes/scan-assets-tab"));
const ScanFormsTab = lazy(() => import("./routes/scan-forms-tab"));
const ScanFormDetail = lazy(() => import("./routes/scan-form-detail"));
const ScanListingsTab = lazy(() => import("./routes/scan-listings-tab"));
const ScanSiteDataTab = lazy(() => import("./routes/scan-site-data-tab"));
const ScanRegionsTab = lazy(() => import("./routes/scan-regions-tab"));
const ScanAiCostsTab = lazy(() => import("./routes/scan-ai-costs-tab"));
const ScanLogsTab = lazy(() => import("./routes/scan-logs-tab"));
const StoragePage = lazy(() => import("./routes/storage"));
const NotFound = lazy(() => import("./routes/not-found"));

const Loading = () => <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

const lazyRoute = (Component: React.LazyExoticComponent<React.ComponentType>) => (

    <Suspense fallback={<Loading />}>
        <Component />
    </Suspense>
);

export const router = createBrowserRouter([
    {
        path: "/",
        element: <Navigate to="/projects" replace />,
    },
    { path: "/login", element: lazyRoute(Login) },
    { path: "/auth/verify", element: lazyRoute(AuthVerify) },
    { path: "/auth/sso/complete", element: lazyRoute(AuthSsoComplete) },
    {
        element: <AppShell />,
        children: [
            { path: "/projects", element: lazyRoute(ProjectsList) },
            { path: "/projects/new", element: lazyRoute(ProjectNew) },
            { path: "/projects/:projectId", element: lazyRoute(ProjectDetail) },
            { path: "/projects/:projectId/edit", element: lazyRoute(ProjectEdit) },
            { path: "/projects/:projectId/seed-urls", element: lazyRoute(ProjectSeedUrls) },
            {
                path: "/scans/:scanId",
                element: lazyRoute(ScanDetailLayout),
                children: [
                    { index: true, element: lazyRoute(ScanDetailIndex) },
                    { path: "pages", element: lazyRoute(ScanPagesTab) },
                    { path: "pages/:pageId", element: lazyRoute(ScanPagesTab) },
                    { path: "assets", element: lazyRoute(ScanAssetsTab) },
                    {
                        path: "forms",
                        element: lazyRoute(ScanFormsTab),
                        children: [
                            { path: ":formId", element: lazyRoute(ScanFormDetail) },
                        ],
                    },
                    { path: "listings", element: lazyRoute(ScanListingsTab) },
                    { path: "site-data", element: lazyRoute(ScanSiteDataTab) },
                    { path: "regions", element: lazyRoute(ScanRegionsTab) },
                    { path: "ai-costs", element: lazyRoute(ScanAiCostsTab) },
                    { path: "logs", element: lazyRoute(ScanLogsTab) },
                ],
            },
            { path: "/storage", element: lazyRoute(StoragePage) },
            { path: "*", element: lazyRoute(NotFound) },
        ],
    },
]);
